import React, { useMemo, useState } from "react";
import { moneyC, titleCase } from "./format.js";
import { Delta, Empty, BizLink } from "./components.jsx";

const catDollar = (b, k) => Math.round(b.total * (b.mix?.[k] || 0));

const COLS = [
  { key: "name", label: "Business" },
  { key: "city", label: "City" },
  { key: "county", label: "County" },
  { key: "permit", label: "Permit", sortable: false },
  { key: "liquor", label: "Liquor", num: true, cls: "col-liquor", get: (b) => catDollar(b, "l") },
  { key: "wine", label: "Wine", num: true, cls: "col-wine", get: (b) => catDollar(b, "w") },
  { key: "beer", label: "Beer", num: true, cls: "col-beer", get: (b) => catDollar(b, "b") },
  { key: "cover", label: "Cover", num: true, cls: "col-cover", get: (b) => catDollar(b, "c") },
  { key: "total", label: "Total Sales", num: true },
  { key: "chg", label: "MoM", num: true },
];

/* Explore Businesses table - sortable, zebra, clickable rows; below 680px it
 * renders as stacked detail cards instead (per handoff breakpoints). */
export default function ExploreTable({ businesses, onOpen, emptyTitle = "No businesses reported", emptyHint }) {
  const [sort, setSort] = useState({ key: "total", dir: -1 });

  const rows = useMemo(() => {
    const col = COLS.find((c) => c.key === sort.key);
    const val = (b) => (col?.get ? col.get(b) : b[sort.key]);
    return [...(businesses || [])].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (typeof av === "string") return av.localeCompare(bv) * sort.dir;
      return ((av ?? 0) - (bv ?? 0)) * sort.dir;
    });
  }, [businesses, sort]);

  if (!rows.length) return <Empty title={emptyTitle}>{emptyHint}</Empty>;

  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));
  const activate = (e, b) => {
    if (e.key === "Enter") { e.preventDefault(); onOpen(b); }
  };

  return (
    <>
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  className={`${c.num ? "num" : ""} ${c.cls || ""} ${c.sortable === false ? "" : "sortable"}`}
                  onClick={c.sortable === false ? undefined : () => toggle(c.key)}
                  aria-sort={sort.key === c.key ? (sort.dir < 0 ? "descending" : "ascending") : undefined}
                >
                  {c.label}
                  {sort.key === c.key && <span className="caret">{sort.dir < 0 ? "▼" : "▲"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="clickable" tabIndex={0} onClick={() => onOpen(b)} onKeyDown={(e) => activate(e, b)}>
                <td className="cell-biz">
                  <BizLink b={b} onOpen={onOpen} className="b-name">{titleCase(b.name)}</BizLink>
                  <div className="b-sub">{titleCase(b.addr)} · {b.cat}</div>
                </td>
                <td>{titleCase(b.city)}</td>
                <td>{b.county}</td>
                <td className="cell-permit">{b.permit}</td>
                <td className="num">{moneyC(catDollar(b, "l"))}</td>
                <td className="num">{moneyC(catDollar(b, "w"))}</td>
                <td className="num">{moneyC(catDollar(b, "b"))}</td>
                <td className="num">{moneyC(catDollar(b, "c"))}</td>
                <td className="num strong">{moneyC(b.total)}</td>
                <td className="num"><Delta v={b.chg} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="stack-cards">
        {rows.map((b) => (
          <BizLink key={b.id} b={b} onOpen={onOpen} className="stack-card">
            <div className="stack-head">
              <span>
                <span className="b-name" style={{ fontWeight: 700, color: "var(--ink)" }}>{titleCase(b.name)}</span>
                <div className="b-sub" style={{ fontSize: 12, color: "var(--muted-2)" }}>
                  {titleCase(b.city)}, {b.county} · {b.cat}
                </div>
              </span>
              <span style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, color: "var(--ink)" }} className="num">{moneyC(b.total)}</div>
                <Delta v={b.chg} />
              </span>
            </div>
            <div className="stack-vals">
              {[["Liquor", "l", "var(--c-liquor)"], ["Wine", "w", "var(--c-wine)"], ["Beer", "b", "var(--c-beer-text)"], ["Cover", "c", "var(--c-cover-text)"]].map(([lab, k, col]) => (
                <div className="stack-val" key={k}>
                  <div className="k" style={{ color: col }}>{lab}</div>
                  <div className="v">{moneyC(catDollar(b, k))}</div>
                </div>
              ))}
            </div>
            <div className="cell-permit" style={{ marginTop: 10 }}>TABC {b.permit}</div>
          </BizLink>
        ))}
      </div>
    </>
  );
}
